<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="basicInfoForm">
      <!-- Name & Provider Row -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('name') }}</label>
          <p class="label-info fixed-label-height">
            Name of the Equipment
          </p>
          <v-text-field
            v-model.trim="equipment.name"
            :rules="minMaxLengthRule(4, 100)"
            class="required"
            outlined
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('provider') }}</label>
          <p class="label-info fixed-label-height">
            {{ $t('equipment-provider-info') }}
          </p>
          <v-autocomplete
            v-if="role === 'super-admin'"
            v-model="autocomplete.provider"
            :items="providers"
            :item-value="'id'"
            :item-text="'name'"
            return-object
            outlined
            clearable
          ></v-autocomplete>
          <v-text-field
            v-else
            v-model="equipment.provider.name"
            outlined
            :disabled="true"
            :rules="requiredRule()"
            class="required"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Manufacturer & ModelType Row -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('equipment-manufacturer') }}</label>
          <p class="label-info">{{ $t('equipment-manufacturer') }}</p>
          <v-text-field
            v-model.trim="equipment.equipmentManufacturer"
            :rules="minMaxLengthRule(4, 100)"
            class="required"
            outlined
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('model-and-type') }}</label>
          <p class="label-info">
            {{ $t('model-and-type') }}
          </p>
          <v-text-field
            v-model.trim="equipment.modelAndType"
            outlined
            :rules="minMaxLengthRule(4, 100)"
            class="required"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Order Row -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>Can be ordered</label>
          <p class="label-info">
            The "order" button is available only in equipments that have this enabled
          </p>
          <v-switch v-model="equipment.orderable" color="success" class="required"></v-switch>
        </v-col>
      </v-row>

      <!-- Optional Basic Info for Edit -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <!-- Prerequisites -->
                <v-row>
                  <v-col cols="12" sm="12" md="12">
                    <label>Prerequisites</label>
                    <p class="label-info">
                      Requirements/prerequisities for the operation or use of the equipment by users
                      (eg the maximum size a 3D printer can operate)
                    </p>
                    <tiptap-vuetify
                      v-model="optionalEquipment.prerequisites"
                      :placeholder="'Max length is 2000 characters'"
                      :extensions="extensions"
                      clearable
                    ></tiptap-vuetify>
                    <div :class="charCount > maxChars ? 'red--text' : 'black--text'">
                      {{ charCount }} / {{ maxChars }} characters
                    </div>
                  </v-col>
                </v-row>

                <!-- Webpage & AvailabilityPolicy -->
                <v-row class="mt-0">
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('webpage') }}</label>
                    <p class="label-info fixed-label-height">
                      {{ $t('webpage-equipment-info') }}
                    </p>
                    <v-text-field
                      v-model.trim="optionalEquipment.webpage"
                      outlined
                      :placeholder="'Paste a URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handleInput"
                    >
                    </v-text-field>
                    <v-alert v-if="!isValidUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('availability-policy') }}</label>
                    <p class="label-info fixed-label-height">
                      {{ $t('availability-equipment-info') }}
                    </p>
                    <v-text-field
                      v-model.trim="optionalEquipment.availabilityPolicy"
                      outlined
                      :placeholder="'Availability Policy'"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Operational Mode -->
                <v-row class="mt-0">
                  <v-col cols="12" md="6">
                    <label>Operational Mode</label>
                    <p class="label-info">
                      How does the equipment operate? Need additional human intervention? Self
                      operated etc.
                    </p>
                    <v-autocomplete
                      v-model="optionalEquipment.operationalMode"
                      :items="operationalModes"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      :placeholder="'Select Mode'"
                      outlined
                      clearable
                      @input="updateOperationalMode"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-if="unsavedChanges && edit"
          @click="updateEquipmentBasicInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-else
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
        >
          {{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Strike,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'EquipmentBasicInfo',
  mixins: [util],
  components: { AlertUnsavedChanges, TiptapVuetify },
  data() {
    return {
      equipment: {
        name: '',
        provider: {
          id: null,
          name: '',
        },
        equipmentManufacturer: '',
        modelAndType: '',
        orderable: true,
      },
      optionalEquipment: {
        prerequisites: '',
        webpage: '',
        availabilityPolicy: '',
        operationalMode: {
          id: null,
          name: '',
        },
      },
      isValidUrl: true,
      dataLoaded: false,
      operationalModes: [],
      initialEquipment: {},
      initialOptionalEquipment: {},
      status: false,
      initialStatus: {},
      unsavedChanges: false,
      role: '',
      providers: [],
      autocomplete: {
        provider: {
          id: null,
          name: '',
        },
      },
      initialAutocompleteProvider: {},
      extensions: [Bold, Italic, Underline, Strike, ListItem, BulletList, OrderedList],
      maxChars: 2000,
    };
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    published: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    equipment: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.equipment.name !== this.initialEquipment.name ||
          this.equipment.equipmentManufacturer !== this.initialEquipment.equipmentManufacturer ||
          this.equipment.modelAndType !== this.initialEquipment.modelAndType ||
          this.equipment.orderable !== this.initialEquipment.orderable;
      },
    },
    optionalEquipment: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.optionalEquipment.webpage !== this.initialOptionalEquipment.webpage ||
          this.optionalEquipment.availabilityPolicy !==
            this.initialOptionalEquipment.availabilityPolicy ||
          this.optionalEquipment.prerequisites !== this.initialOptionalEquipment.prerequisites ||
          this.optionalEquipment.operationalMode.id !==
            this.initialOptionalEquipment.operationalMode.id ||
          this.optionalEquipment.operationalMode.name !==
            this.initialOptionalEquipment.operationalMode.name;
      },
    },
    published: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges = this.status !== this.published;
      },
    },
    autocomplete: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.autocomplete.provider.id !== this.initialAutocompleteProvider.provider.id ||
          this.autocomplete.provider.name !== this.initialAutocompleteProvider.provider.name;
      },
    },
  },
  mounted() {
    this.role = localStorage.getItem('roles') || '';
    console.log('role:', this.role);

    if (this.role === 'super-admin') {
      this.axios
        .get('/api/v1/providers/all-light')
        .then(res => {
          this.providers = res.data;
        })
        .finally(() => {
          this.loadingComponents++;
        });
    } else {
      this.axios
        .get('/api/v1/provider-settings/' + sessionStorage.getItem('providerId') + '/light')
        .then(res => {
          this.equipment.provider.name = res.data.name;
          this.equipment.provider.id = res.data.id;
        });
    }
    if (this.edit) {
      this.getEquipmentIdBasicInfo();
      this.getOperationalModes();
    }
  },
  computed: {
    charCount() {
      const div = document.createElement('div');
      div.innerHTML = this.optionalEquipment.prerequisites || '';
      return div.textContent.length;
    },
  },
  methods: {
    updateOperationalMode(value) {
      this.optionalEquipment.operationalMode = value || { id: null, name: '' };
    },
    next() {
      const isFormValid = this.$refs.basicInfoForm.validate();
      if (isFormValid) {
        this.$emit('tab', 1);
        this.$emit('equipmentBasicInfo', { ...this.equipment });
      }
    },
    getEquipmentIdBasicInfo() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get(`/api/v1/equipment/${id}`)
        .then(response => {
          console.log('Get Equipment Basic Info', response.data);

          this.status = response.data.published;
          // this.$emit('status', this.status);
          this.equipment.name = response.data.name;
          this.equipment.equipmentManufacturer = response.data.equipmentManufacturer;
          this.equipment.modelAndType = response.data.modelAndType;
          this.equipment.orderable = response.data.orderable;
          this.optionalEquipment.webpage = response.data.webpage;
          this.optionalEquipment.availabilityPolicy = response.data.availabilityPolicy;
          this.optionalEquipment.prerequisites = response.data.prerequisites;

          if (this.role === 'super-admin') {
            this.autocomplete.provider = {
              id: response.data.provider.id,
              name: response.data.provider.name,
            };

            this.initialAutocompleteProvider = {
              provider: {
                id: response.data.provider.id,
                name: response.data.provider.name,
              },
            };
          }

          if (response.data.operationalMode) {
            this.optionalEquipment.operationalMode = {
              id: response.data.operationalMode.id,
              name: response.data.operationalMode.name,
            };
          } else {
            this.optionalEquipment.operationalMode = {
              id: null,
              name: '',
            };
          }

          this.initialEquipment = {
            name: response.data.name,
            equipmentManufacturer: response.data.equipmentManufacturer,
            modelAndType: response.data.modelAndType,
            orderable: response.data.orderable,
          };
          this.initialOptionalEquipment = {
            webpage: response.data.webpage,
            availabilityPolicy: response.data.availabilityPolicy,
            prerequisites: response.data.prerequisites,
            operationalMode: {
              id: response.data.operationalMode ? response.data.operationalMode.id : null,
              name: response.data.operationalMode ? response.data.operationalMode.name : '',
            },
          };
          this.initialStatus = {
            published: response.data.published,
          };
          this.$emit('status', this.initialStatus.published);
          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching equipment data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    updateEquipmentBasicInfo() {
      const id = this.$route.params.id;

      if (this.role === 'super-admin') {
        const equipmentUpdateData = {
          published: this.published,
          id: id,
          name: this.equipment.name,
          equipmentManufacturer: this.equipment.equipmentManufacturer,
          modelAndType: this.equipment.modelAndType,
          orderable: this.equipment.orderable,
          provider: {
            id: this.autocomplete.provider.id,
            name: this.autocomplete.provider.name,
          },
          webpage: this.optionalEquipment.webpage,
          availabilityPolicy: this.optionalEquipment.availabilityPolicy,
          prerequisites: this.optionalEquipment.prerequisites,
          operationalMode:
            this.optionalEquipment.operationalMode &&
            this.optionalEquipment.operationalMode.id &&
            this.optionalEquipment.operationalMode.name
              ? {
                  id: this.optionalEquipment.operationalMode.id,
                  name: this.optionalEquipment.operationalMode.name,
                }
              : null,
        };

        this.deleteEmptyProperties(equipmentUpdateData);

        const isFormValid = this.$refs.basicInfoForm.validate();
        if (isFormValid) {
          this.axios
            .put('api/v1/equipment/basic-info', equipmentUpdateData)
            .then(response => {
              console.log('Equipment info updated successfully:', response.data);
              EventBus.$emit('snackbar', {
                text: 'Equipment Updated Successfully',
              });
              this.initialEquipment = { ...this.equipment };
              this.initialOptionalEquipment = { ...this.optionalEquipment };
              this.initialStatus = this.status;
              this.initialAutocompleteProvider = { ...this.autocomplete };
              this.unsavedChanges = false;
              this.$emit('updatedChanges', true);

              this.getEquipmentIdBasicInfo();
            })
            .catch(error => {
              console.error('Error updating equipment info:', error);
            });
        }
      }

      if (this.role === 'provider') {
        const equipmentUpdateData = {
          published: this.published,
          id: id,
          name: this.equipment.name,
          equipmentManufacturer: this.equipment.equipmentManufacturer,
          modelAndType: this.equipment.modelAndType,
          orderable: this.equipment.orderable,
          provider: this.equipment.provider,
          webpage: this.optionalEquipment.webpage,
          availabilityPolicy: this.optionalEquipment.availabilityPolicy,
          prerequisites: this.optionalEquipment.prerequisites,
          operationalMode:
            this.optionalEquipment.operationalMode &&
            this.optionalEquipment.operationalMode.id &&
            this.optionalEquipment.operationalMode.name
              ? {
                  id: this.optionalEquipment.operationalMode.id,
                  name: this.optionalEquipment.operationalMode.name,
                }
              : null,
        };

        this.deleteEmptyProperties(equipmentUpdateData);

        const isFormValid = this.$refs.basicInfoForm.validate();
        if (isFormValid) {
          this.axios
            .put('api/v1/equipment/basic-info', equipmentUpdateData)
            .then(response => {
              console.log('Equipment info updated successfully:', response.data);
              EventBus.$emit('snackbar', {
                text: 'Equipment Updated Successfully',
              });
              this.initialEquipment = { ...this.equipment };
              this.initialOptionalEquipment = { ...this.optionalEquipment };
              this.initialStatus = this.status;
              this.unsavedChanges = false;
              this.$emit('updatedChanges', true);

              this.getEquipmentIdBasicInfo();
            })
            .catch(error => {
              console.error('Error updating equipment info:', error);
            });
        }
      }
    },
    cancel() {
      this.getEquipmentIdBasicInfo();
    },

    getOperationalModes() {
      return this.axios
        .get('/api/v1/operational-modes')
        .then(response => {
          this.operationalModes = response.data.content ?? [];
        })
        .catch(error => {
          console.error('Error fetching operational modes:', error);
          this.operationalModes = [];
        });
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    handleInput(value) {
      if (value) {
        this.validateUrl(value);
      } else {
        this.isValidUrl = true;
      }
    },
    validateUrl() {
      this.isValidUrl = this.canParse(this.optionalEquipment.webpage);
    },
    canParse(link) {
      if (!/^https?:\/\//i.test(link)) {
        return false;
      }
      try {
        new URL(link);
        return true;
      } catch (e) {
        return false;
      }
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 4em;
}
</style>
